import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Grid,
  FormControl
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';

import ObjectiveEventLog from './EventLogItem';
import CommentLogItem from './CommentLogItem';
import KpiLogItem from './KpiLogItem';
import DeletedKpiEntryLogs from './DeletedKpiEntryLogs';
import LoadMoreButton from '../../LoadMoreButton';
import { EVENT_LOG_LIMIT, EVENT_LOG, OWNER_TYPES } from '../../../../utils/const';
import { fetchObjectiveEventLog } from '../../../../api/objective/objective.api';
import { addAlert } from '../../../../actions/alerts.action';

const Panel = ({ objective, createdAt, enabled, owner }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [ subjectType, setSubjectType ] = useState(EVENT_LOG.OBJECTIVES);
  const [ open, setOpen ] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleSelectResource = (event, selectedResource) => {
    setSubjectType(selectedResource);
  };

  const objectiveEventsQuery = useInfiniteQuery(
    [ 'objectiveLogs', objective.id, subjectType ],
    async ({ pageParam = 1 }) => {
      const { data } = await fetchObjectiveEventLog({
        objectiveId: objective?.id,
        createdAt,
        pageNumber: pageParam,
        subjectType
      });
      return data;
    },
    {
      getNextPageParam: (lastPage, pages) => {
        return lastPage?.length % EVENT_LOG_LIMIT === 0 && lastPage.length !== 0 ?
          (pages?.flat().length / EVENT_LOG_LIMIT) + 1 : undefined;
      },
      enabled,
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  const objectiveEventLogs = objectiveEventsQuery.data?.pages.flat() ?? [];
  const eventIsLoading =  objectiveEventsQuery.isFetching;

  const renderResourceTrails = () => {
    switch (subjectType) {
      case EVENT_LOG.OBJECTIVES:
        return objectiveEventLogs.map((event, index) => (
          <ObjectiveEventLog
            key={ index }
            event={ event }
          />
        ));
      case EVENT_LOG.COMMENTS:
        return objectiveEventLogs.map((event, index) => (
          <CommentLogItem
            key={ index }
            event={ event }
          />
        ));
      case EVENT_LOG.KPIS:
        return objectiveEventLogs.map((event, index) => (
          <div key={ index }>
            <KpiLogItem
              key={ index }
              event={ event }
              handleOpen={ handleOpen }
            />
            <DeletedKpiEntryLogs
              open={ open }
              setOpen={ setOpen }
              objective={ objective }
              kpi={ event }
            />
          </div>
        ));
      default:
        return objectiveEventLogs.map((event, index) => (
          <ObjectiveEventLog
            key={ index }
            event={ event }
          />
        ));
    }
  };

  return (
    <>
      {owner.type === OWNER_TYPES.EMPLOYEE &&
      <FormControl component="fieldset" >
        <ToggleButtonGroup
          value={ subjectType }
          exclusive
          onChange={ handleSelectResource }
          aria-label="text alignment"
        >
          <ToggleButton value={ EVENT_LOG.OBJECTIVES }>
            { t('objective.eventlog.type.general') }
          </ToggleButton>
          <ToggleButton value={ EVENT_LOG.COMMENTS }>
            { t('objective.eventlog.type.comment') }
          </ToggleButton>
          <ToggleButton value={ EVENT_LOG.KPIS }>
            { t('objective.eventlog.type.kpi') }
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      }
      {renderResourceTrails()}
      <Grid container justifyContent="center">
        <LoadMoreButton
          loading={ eventIsLoading }
          canLoadMore={ objectiveEventsQuery.hasNextPage }
          loadMore={ objectiveEventsQuery.fetchNextPage }
          loadMoreTranslation={ t('objective.event.loadMoreBtn') }
          noMoreToLoadTranslation={ t('objective.event.NoMoreEvents') }
        />
      </Grid>
    </>
  );
};

export default Panel;